import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import { Check } from 'lucide-react';

const buttonStyles = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium',
    'cursor-pointer',
    'transition-[color,background-color,border-color,box-shadow,transform] duration-150 motion-reduce:transform-none',
    'outline-none',
    'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25',
    'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
    'disabled:pointer-events-none disabled:opacity-50',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
    'active:scale-[0.98]',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-action text-white border-action-foreground/10 hover:bg-action-hover border shadow-xs',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        outline:
          'border-border bg-card text-foreground border shadow-xs hover:bg-muted',
        secondary:
          'border-border/80 bg-muted text-foreground border shadow-none hover:bg-muted/75',
        ghost:
          'text-muted-foreground shadow-none hover:bg-muted hover:text-foreground',
        link: 'text-primary shadow-none underline-offset-4 hover:underline',
        glow: cn(
          'rounded-full border border-primary/35 bg-action text-action-foreground shadow-lg shadow-primary/25',
          'hover:bg-action-hover hover:-translate-y-px hover:shadow-xl'
        ),
        eclipse: cn(
          'relative isolate bg-umbra text-white font-semibold border border-primary shadow-[0_8px_24px_rgba(17,17,17,0.16)]',
          'after:pointer-events-none after:absolute after:inset-x-1 after:top-1/2 after:bottom-0 after:-z-20 after:rounded-full after:bg-[radial-gradient(circle_at_center,rgba(255,176,0,0.38),rgba(255,176,0,0)_72%)] after:blur-xl after:opacity-80 after:transition-all after:duration-300',
          'transition-transform hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(255,176,0,0.22)] hover:after:inset-x-0 hover:after:opacity-100'
        ),
      },
      size: {
        default:
          'h-9 px-4 py-2 has-[>svg]:px-3 has-[[data-slot=button-label]>svg]:px-3',
        xs: 'h-7 gap-1 px-2 text-xs has-[>svg]:px-1.5 has-[[data-slot=button-label]>svg]:px-1.5',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5 has-[[data-slot=button-label]>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4 has-[[data-slot=button-label]>svg]:px-4',
        icon: 'size-9',
        'icon-xs': 'size-7',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function buttonVariants(props: Parameters<typeof buttonStyles>[0] = {}) {
  const { class: classValue, className, ...variants } = props ?? {};
  const base = buttonStyles(variants);
  const overrides = cn(classValue, className);
  const classes = cn(base, overrides);
  // Standard actions use the shared foreground. Custom backgrounds retain
  // their own label color, and callers can still choose a label color.
  return cn(
    base,
    classes.split(' ').includes('bg-action') && 'text-action-foreground',
    overrides
  );
}

interface ButtonProps
  extends
    React.ComponentPropsWithRef<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
  /** Set only after the action succeeds; clear when the form changes. */
  isSuccess?: boolean;
  successLabel?: string;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  loadingLabel = 'Loading',
  isSuccess = false,
  successLabel = 'Saved',
  children,
  disabled,
  ref,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      data-variant={variant ?? 'default'}
      data-size={size ?? 'default'}
      data-feedback={isLoading ? 'loading' : isSuccess ? 'success' : 'idle'}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          <span
            data-slot="button-label"
            className={cn(
              'inline-flex w-full [flex-direction:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit] transition-opacity duration-150',
              (isLoading || isSuccess) && 'opacity-0'
            )}
          >
            {children}
          </span>
          {(isLoading || isSuccess) && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              {isLoading ? (
                <Spinner
                  size="sm"
                  className="text-current"
                  label={loadingLabel}
                />
              ) : (
                <span
                  role="status"
                  className="inline-flex items-center gap-1.5 text-xs"
                >
                  <Check className="size-4" aria-hidden="true" />
                  <span className="sr-only">{successLabel}</span>
                </span>
              )}
            </span>
          )}
        </>
      )}
    </Comp>
  );
}
Button.displayName = 'Button';

export { Button, buttonVariants };
